/**
 * Sections Library API
 *
 * RTK Query API wrapper for the sections library feature.
 * Enhances the generated API with proper cache tagging for
 * automatic cache invalidation on mutations.
 */

import { enhancedApi } from '@store/generated/templates';

/**
 * Feature-specific tag types for cache management
 */
const SECTIONS_TAG_TYPES = ['Section', 'SectionLibrary'] as const;

/**
 * Sections Library API - extends the generated API with custom cache tagging
 */
export const sectionsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: SECTIONS_TAG_TYPES,
  endpoints: {
    // List all accessible sections
    getV2ProviderSectionsLibrary: {
      providesTags: result =>
        result?.sections
          ? [
              ...result.sections.map((s: any) => ({
                type: 'Section' as const,
                id: s.section.id,
              })),
              { type: 'SectionLibrary' as const, id: 'LIST' },
            ]
          : [{ type: 'SectionLibrary' as const, id: 'LIST' }],
    },

    // Get single section
    getV2ProviderSectionsLibraryBySectionId: {
      providesTags: (_result, _error, { sectionId }: any) => [
        { type: 'Section' as const, id: sectionId },
        { type: 'SectionLibrary' as const, id: 'LIST' },
      ],
    },

    // Search sections
    postV2ProviderSectionsLibrarySearch: {
      // Mutations should use invalidatesTags or nothing if they don't modify data.
      // Search is a POST but it's a read operation in this case.
      // However, RTK Query treats all mutations as potentially modifying data.
      // Since this is a search, we don't need to invalidate anything.
    },

    // Fork section (creates a USER-scoped copy)
    postV2ProviderSectionsLibraryBySectionIdFork: {
      invalidatesTags: [{ type: 'SectionLibrary' as const, id: 'LIST' }],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Queries
  useGetV2ProviderSectionsLibraryQuery: useGetSectionLibraryQuery,
  useLazyGetV2ProviderSectionsLibraryQuery: useLazyGetSectionLibraryQuery,
  useGetV2ProviderSectionsLibraryBySectionIdQuery: useGetSectionByIdQuery,
  useLazyGetV2ProviderSectionsLibraryBySectionIdQuery: useLazyGetSectionByIdQuery,

  // Mutations
  usePostV2ProviderSectionsLibrarySearchMutation: useSearchSectionsMutation,
  usePostV2ProviderSectionsLibraryBySectionIdForkMutation: useForkSectionMutation,
} = sectionsApi;

